import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { INK, MUTED } from './constants';

/** Max rows of chips shown before the row collapses behind a "+N more" link. */
const MAX_COLLAPSED_ROWS = 2;
/** Flex `gap` between chips, in px. Kept as a constant because the fit maths needs it. */
const CHIP_GAP = 4;
/** Slack so a one-character change in the link's text ("+9" → "+12") can't clip it. */
const MORE_LINK_PAD = 10;

/** "Flavor" → "flavors", "Size" → "sizes", "Quantity" → "quantities". */
function pluralizeLower(label: string): string {
  const word = label.trim().toLowerCase();
  if (!word) return word;
  if (word.endsWith('s')) return word;
  if (/[^aeiou]y$/.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
}

/**
 * How many chips fit in `maxRows`, reserving `reserveWidth` on the LAST allowed
 * row for the "+N more" link.
 *
 * This simulates the wrap rather than reading it back from the DOM, because
 * reading it back is circular: hiding the overflow chips changes the very layout
 * the decision was made from, so the count oscillates. Chip widths are fixed by
 * their text (`white-space: nowrap`), so they survive a container resize and one
 * measurement of each is enough forever.
 */
export function countChipsThatFit(
  containerWidth: number,
  chipWidths: number[],
  reserveWidth: number,
  maxRows = MAX_COLLAPSED_ROWS,
  gap = CHIP_GAP,
): number {
  if (containerWidth <= 0) return chipWidths.length;

  const availableOn = (row: number) =>
    row === maxRows - 1 && reserveWidth > 0 ? containerWidth - gap - reserveWidth : containerWidth;

  let row = 0;
  let used = 0;
  let fitted = 0;

  for (const width of chipWidths) {
    const needed = used === 0 ? width : gap + width;
    if (used + needed <= availableOn(row)) {
      used += needed;
      fitted += 1;
      continue;
    }

    // A chip that does not fit an EMPTY row is wider than the row itself. Wrapping
    // it would loop forever and leave a blank row behind, so it owns this row.
    if (used === 0) {
      used = width;
      fitted += 1;
      continue;
    }

    row += 1;
    if (row >= maxRows) return fitted;

    if (width > availableOn(row)) {
      // It does not fit the fresh row either. If the RESERVATION is what blocks
      // it, it genuinely belongs on a row we do not have — stop. Otherwise it is
      // an oversized chip again, and the rule above applies.
      if (row === maxRows - 1 && reserveWidth > 0) return fitted;
    }
    used = width;
    fitted += 1;
  }
  return fitted;
}

export interface VariantOption<T> {
  value: string;
  variant: T | undefined;
  isAvailable: boolean;
}

/**
 * One variation dimension's chips — e.g. every Size, or every Flavor.
 *
 * These used to be a single no-wrap row with `overflow-x: auto`. On a 396px
 * panel that hides most of the options behind a scrollbar the shopper cannot
 * see, on an axis (horizontal, inside a vertically-scrolling panel) they have no
 * reason to try: a product with eight flavors showed three.
 *
 * So the chips WRAP, and the row shows at most {@link MAX_COLLAPSED_ROWS} of
 * them with a "+N more flavors" link at the end of the last one, which expands
 * the rest in place.
 */
export function VariantOptionRow<T>({
  label,
  selected,
  options,
  onSelect,
}: {
  label: string;
  selected?: string;
  options: VariantOption<T>[];
  onSelect: (variant: T) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chipRefs = useRef<Array<HTMLElement | null>>([]);
  const moreRef = useRef<HTMLButtonElement | null>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  // `null` = not measured yet, which renders every chip. A first paint showing
  // too many for one frame beats one showing too few.
  const [chipWidths, setChipWidths] = useState<number[] | null>(null);
  const [moreWidth, setMoreWidth] = useState(0);

  const measureWidths = useCallback(() => {
    const widths = chipRefs.current.slice(0, options.length).map((el) => el?.offsetWidth ?? 0);
    if (widths.some((w) => w === 0)) return; // not laid out yet
    setChipWidths((prev) =>
      prev && prev.length === widths.length && prev.every((w, i) => w === widths[i]) ? prev : widths,
    );
  }, [options.length]);

  useLayoutEffect(() => {
    measureWidths();
  }, [measureWidths, options]);

  // The link's own width, measured once it exists — it only renders when there
  // IS overflow, and its width is what decides how much overflow there is. The
  // value only ever GROWS: its text changes with the count ("+9" → "+12"), so
  // letting it shrink would let the two chase each other forever.
  useLayoutEffect(() => {
    const width = moreRef.current?.offsetWidth ?? 0;
    if (width <= 0) return;
    setMoreWidth((prev) => Math.max(prev, width + MORE_LINK_PAD));
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    setContainerWidth(container.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      setContainerWidth(container.clientWidth);
      measureWidths();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [measureWidths]);

  // Two passes, because the link only costs room when it is actually needed: fit
  // with no reservation first, and only if that leaves chips over does the link
  // exist — then refit with room for it.
  let visibleCount = options.length;
  if (chipWidths && chipWidths.length === options.length && containerWidth > 0) {
    const withoutLink = countChipsThatFit(containerWidth, chipWidths, 0);
    visibleCount =
      withoutLink >= options.length ? options.length : countChipsThatFit(containerWidth, chipWidths, moreWidth);
  }

  const hiddenCount = Math.max(0, options.length - visibleCount);
  const isCollapsed = !isExpanded && hiddenCount > 0;
  const showMoreLink = hiddenCount > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{ margin: 0, fontSize: 14, fontWeight: 500, lineHeight: '24px', color: INK }}>
        {label}
        {selected ? `: ${selected}` : ''}
      </p>
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: CHIP_GAP,
        }}
      >
        {options.map(({ value, variant, isAvailable }, index) => {
          const isSelected = value === selected;
          const isUnavailable = !isAvailable;
          const isHidden = isCollapsed && index >= visibleCount;
          return (
            <button
              key={value}
              ref={(el) => {
                chipRefs.current[index] = el;
              }}
              type="button"
              disabled={isSelected || !isAvailable}
              onClick={() => variant && onSelect(variant)}
              style={{
                display: isHidden ? 'none' : 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px 12px',
                borderRadius: 100,
                border: `1px solid ${isSelected ? INK : '#DCDDDC'}`,
                background: '#fff',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: '20px',
                color: isUnavailable ? MUTED : INK,
                opacity: isUnavailable ? 0.4 : 1,
                cursor: isSelected || !isAvailable ? 'default' : 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </button>
          );
        })}

        {showMoreLink && (
          <button
            ref={moreRef}
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 4px',
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: '20px',
              color: INK,
              textDecoration: 'underline',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {isExpanded ? 'Show less' : `+${hiddenCount} more ${pluralizeLower(label)}`}
          </button>
        )}
      </div>
    </div>
  );
}
